import { useState, useEffect } from 'react';
import { ArrowLeft, Save, Wrench, Truck as TruckIcon, DollarSign, MapPin, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Truck {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  year: number;
}

interface Trailer {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  year: number;
}

interface EditMaintenanceLogPageProps {
  logId: string;
  onBack: () => void;
  onSuccess: () => void;
}

export function EditMaintenanceLogPage({ logId, onBack, onSuccess }: EditMaintenanceLogPageProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [truckSearch, setTruckSearch] = useState('');
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [drivers, setDrivers] = useState<Array<{ id: string; first_name: string; last_name: string; email: string; phone_number?: string }>>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  const [formData, setFormData] = useState({
    maintenanceType: 'Preventive Maintenance',
    truckId: '',
    serviceProvider: '',
    providerEmail: '',
    providerPhone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    serviceDate: '',
    odometer: '',
    description: '',
    cost: '',
    companyCostAmount: '',
    paidBy: 'company',
    driverPaymentAmount: '',
    driverId: '',
    glAccount: '5100-Maintenance',
  });

  const maintenanceTypes = [
    'Preventive Maintenance',
    'Oil Change',
    'Tire Service',
    'Brake Service',
    'Engine Repair',
    'Transmission Repair',
    'Electrical Repair',
    'Body Work',
    'DOT Inspection',
    'Annual Inspection',
    'Other',
  ];

  const glAccounts = [
    '5100-Maintenance',
    '5110-Repairs',
    '5120-Tires',
    '5130-Oil & Lubricants',
    '5140-Parts',
    '5150-Labor',
    '5160-Inspections',
    '5900-Other Maintenance',
  ];

  useEffect(() => {
    loadData();
  }, [logId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.truck-search-container')) {
        setShowTruckDropdown(false);
      }
      if (!target.closest('.driver-search-container')) {
        setShowDriverDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadDrivers() {
    setLoadingDrivers(true);
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, email, phone_number')
        .not('status', 'is', null)
        .order('last_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoadingDrivers(false);
    }
  }

  async function loadData() {
    loadDrivers();
    try {
      // Load trucks
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, year')
        .eq('status', 'in_service')
        .order('unit_number');

      setTrucks(trucksData || []);

      // Load trailers
      const { data: trailersData } = await supabase
        .from('trailers')
        .select('id, unit_number, make, model, year')
        .in('status', ['available', 'in_use'])
        .order('unit_number');

      setTrailers(trailersData || []);

      // Load maintenance log
      const { data: logData, error } = await supabase
        .from('maintenance_logs')
        .select('*')
        .eq('id', logId)
        .single();

      if (error) throw error;

      setFormData({
        maintenanceType: logData.maintenance_type || 'Preventive Maintenance',
        truckId: logData.truck_id || '',
        serviceProvider: logData.service_provider || '',
        providerEmail: logData.provider_email || '',
        providerPhone: logData.provider_phone || '',
        street: logData.street || '',
        city: logData.city || '',
        state: logData.state || '',
        zipCode: logData.zip_code || '',
        serviceDate: logData.service_date || '',
        odometer: logData.odometer?.toString() || '',
        description: logData.description || '',
        cost: logData.cost?.toString() || '',
        companyCostAmount: logData.company_cost_amount?.toString() || '',
        paidBy: logData.paid_by || 'company',
        driverPaymentAmount: logData.driver_payment_amount?.toString() || '',
        driverId: logData.driver_id || '',
        glAccount: logData.gl_account || '5100-Maintenance',
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load maintenance log');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!formData.truckId) {
      toast.error('Please select a truck');
      return;
    }

    if (!formData.serviceDate) {
      toast.error('Please select a service date');
      return;
    }

    if (!formData.companyCostAmount) {
      toast.error('Please enter company cost amount');
      return;
    }

    if (formData.paidBy === 'driver' && (!formData.driverPaymentAmount || parseFloat(formData.driverPaymentAmount) <= 0)) {
      toast.error('Please enter driver payment amount');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('maintenance_logs')
        .update({
          maintenance_type: formData.maintenanceType,
          truck_id: formData.truckId,
          service_provider: formData.serviceProvider || null,
          provider_email: formData.providerEmail || null,
          provider_phone: formData.providerPhone || null,
          street: formData.street || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zipCode || null,
          service_date: formData.serviceDate,
          odometer: formData.odometer ? parseInt(formData.odometer) : null,
          description: formData.description || null,
          cost: formData.cost ? parseFloat(formData.cost) : 0,
          company_cost_amount: parseFloat(formData.companyCostAmount),
          paid_by: formData.paidBy,
          driver_payment_amount: formData.driverPaymentAmount ? parseFloat(formData.driverPaymentAmount) : null,
          driver_id: formData.paidBy === 'driver' ? formData.driverId : null,
          gl_account: formData.glAccount,
        })
        .eq('id', logId);

      if (error) throw error;

      toast.success('Maintenance log updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating maintenance log:', error);
      toast.error('Failed to update maintenance log');
    } finally {
      setSaving(false);
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calculate company cost amount based on payment split
      if (field === 'paidBy' || field === 'cost' || field === 'driverPaymentAmount') {
        const totalCost = parseFloat(newData.cost) || 0;
        const driverPayment = parseFloat(newData.driverPaymentAmount) || 0;
        
        if (newData.paidBy === 'company' || newData.paidBy === 'tbd') {
          // Company pays everything (TBD defaults to company for now)
          newData.companyCostAmount = totalCost.toString();
          newData.driverPaymentAmount = '';
        } else if (newData.paidBy === 'driver') {
          // Driver pays specified amount, company pays the rest
          newData.companyCostAmount = Math.max(0, totalCost - driverPayment).toString();
        }
      }
      
      return newData;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Edit Maintenance Log</h1>
                <p className="text-sm text-slate-600">Update maintenance activity details</p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Form - Same as Create Page */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Wrench className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Maintenance Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.maintenanceType}
                  onChange={(e) => handleChange('maintenanceType', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {maintenanceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="relative truck-search-container">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Truck <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={truckSearch || (formData.truckId ? trucks.find(t => t.id === formData.truckId)?.unit_number || '' : '')}
                    onChange={(e) => {
                      setTruckSearch(e.target.value);
                      setShowTruckDropdown(true);
                      if (!e.target.value) {
                        handleChange('truckId', '');
                      }
                    }}
                    onFocus={() => setShowTruckDropdown(true)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search trucks by unit number..."
                  />
                </div>
                {showTruckDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {trucks
                      .filter(truck => 
                        truck.unit_number.toLowerCase().includes(truckSearch.toLowerCase()) ||
                        `${truck.make} ${truck.model}`.toLowerCase().includes(truckSearch.toLowerCase())
                      )
                      .map(truck => (
                        <button
                          key={truck.id}
                          type="button"
                          onClick={() => {
                            handleChange('truckId', truck.id);
                            setTruckSearch(truck.unit_number);
                            setShowTruckDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                        >
                          <div className="font-semibold text-slate-900">{truck.unit_number}</div>
                          <div className="text-sm text-slate-600">{truck.year} {truck.make} {truck.model}</div>
                        </button>
                      ))}
                    {trucks.filter(truck => 
                      truck.unit_number.toLowerCase().includes(truckSearch.toLowerCase()) ||
                      `${truck.make} ${truck.model}`.toLowerCase().includes(truckSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        No trucks found
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Service Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.serviceDate}
                  onChange={(e) => handleChange('serviceDate', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Odometer
                </label>
                <input
                  type="number"
                  value={formData.odometer}
                  onChange={(e) => handleChange('odometer', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Current mileage"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Service Provider */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <TruckIcon className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Provider</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={formData.serviceProvider}
                  onChange={(e) => handleChange('serviceProvider', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ABC Truck Repair"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Email
                </label>
                <input
                  type="email"
                  value={formData.providerEmail}
                  onChange={(e) => handleChange('providerEmail', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="service@provider.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Provider Phone
                </label>
                <input
                  type="tel"
                  value={formData.providerPhone}
                  onChange={(e) => handleChange('providerPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Service Location */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Service Location</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Service Road"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Chicago"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="IL"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Zip Code
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="60601"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description
              <span className="text-slate-500 ml-2 text-xs">Max length: 2000 characters</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              maxLength={2000}
              placeholder="Describe the maintenance work performed..."
            />
            <div className="text-right text-xs text-slate-500 mt-1">
              {formData.description.length} / 2000 characters
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-orange-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Financial Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Total Cost
                </label>
                <input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => handleChange('cost', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  GL Account <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.glAccount}
                  onChange={(e) => handleChange('glAccount', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {glAccounts.map(account => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Payment Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Who is paying?
                  </label>
                  <select
                    value={formData.paidBy}
                    onChange={(e) => handleChange('paidBy', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="company">Company</option>
                    <option value="driver">Driver</option>
                    <option value="tbd">TBD (To Be Decided)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Driver Payment Amount {formData.paidBy === 'driver' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    value={formData.driverPaymentAmount}
                    onChange={(e) => handleChange('driverPaymentAmount', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={formData.paidBy === 'company' || formData.paidBy === 'tbd'}
                  />
                  {formData.paidBy === 'company' && (
                    <p className="text-xs text-slate-500 mt-1">Company pays full amount</p>
                  )}
                  {formData.paidBy === 'tbd' && (
                    <p className="text-xs text-amber-500 mt-1">Payment responsibility to be decided</p>
                  )}
                </div>
              </div>

              {/* Driver Selection - Only show when driver is paying */}
              {formData.paidBy === 'driver' && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Driver <span className="text-red-500">*</span>
                  </label>
                  <div className="relative driver-search-container">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={driverSearch || (formData.driverId ? drivers.find(d => d.id === formData.driverId)?.first_name + ' ' + drivers.find(d => d.id === formData.driverId)?.last_name || '' : '')}
                      onChange={(e) => {
                        setDriverSearch(e.target.value);
                        setShowDriverDropdown(true);
                        if (!e.target.value) {
                          handleChange('driverId', '');
                        }
                      }}
                      onFocus={() => setShowDriverDropdown(true)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search drivers by name..."
                      disabled={loadingDrivers}
                    />
                    {showDriverDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {drivers
                          .filter(driver => 
                            driver.first_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                            driver.last_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                            `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                          )
                          .map(driver => (
                            <button
                              key={driver.id}
                              type="button"
                              onClick={() => {
                                handleChange('driverId', driver.id);
                                setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                                setShowDriverDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                            >
                              <div className="font-semibold text-slate-900">{driver.first_name} {driver.last_name}</div>
                              <div className="text-sm text-slate-600">
                                {driver.email || driver.phone_number || 'No contact info'}
                              </div>
                            </button>
                          ))}
                        {drivers.filter(driver => 
                          driver.first_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                          driver.last_name.toLowerCase().includes(driverSearch.toLowerCase()) ||
                          `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-500 text-center">
                            No drivers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Cost Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.companyCostAmount}
                    onChange={(e) => handleChange('companyCostAmount', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    readOnly
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.paidBy === 'company'
                      ? 'Company pays full amount'
                      : formData.paidBy === 'tbd'
                        ? 'Payment responsibility to be decided'
                        : `Total: $${parseFloat(formData.cost || '0').toFixed(2)} - Driver: $${parseFloat(formData.driverPaymentAmount || '0').toFixed(2)}`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
